import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Loader2, X } from 'lucide-react';
import { getAgentDocumentTypeLabel } from '../services/agentDocumentHandoff';
import { persistAgentDocumentDraft } from '../utils/agentDocumentDraftView';

function buildEmbedUrl(openPath) {
  if (!openPath || typeof window === 'undefined') return '';
  const base = `${window.location.origin}${window.location.pathname}`;
  const hashPath = openPath.startsWith('/') ? openPath : `/${openPath}`;
  const withEmbed = hashPath.includes('embed=agent')
    ? hashPath
    : `${hashPath}${hashPath.includes('?') ? '&' : '?'}embed=agent`;
  return `${base}#${withEmbed}`;
}

export default function AIAgentDocumentEmbedPanel({
  draft,
  onClose,
  onDocumentSaved,
  lightSurface = false,
  variant = 'panel',
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.documentEmbedPanel.${key}`, opts);
  const embedSrc = useMemo(() => buildEmbedUrl(draft?.openPath), [draft?.openPath]);
  const [isFrameLoading, setIsFrameLoading] = useState(true);
  const docTypeLabel = draft ? getAgentDocumentTypeLabel(draft.docType) : '';

  useLayoutEffect(() => {
    if (draft?.conversionData) {
      persistAgentDocumentDraft(draft.conversionData);
    }
  }, [draft]);

  useEffect(() => {
    setIsFrameLoading(true);
  }, [embedSrc]);

  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      const payload = event.data || {};
      if (payload.type === 'agent-document-embed-close') {
        onClose?.();
      }
      if (payload.type === 'agent-document-saved') {
        onDocumentSaved?.(payload);
      }
      if (payload.type === 'agent-document-embed-ready') {
        setIsFrameLoading(false);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onClose, onDocumentSaved]);

  useEffect(() => {
    if (!embedSrc) return undefined;
    const fallbackTimer = window.setTimeout(() => {
      setIsFrameLoading(false);
    }, 12000);
    return () => window.clearTimeout(fallbackTimer);
  }, [embedSrc]);

  if (!draft || !embedSrc) return null;

  const isModal = variant === 'modal';
  const panelClass = lightSurface
    ? 'bg-gray-50 border-gray-200 text-gray-900'
    : isModal
      ? 'bg-transparent text-white'
      : 'bg-slate-950/60 border-white/10 text-white';

  return (
    <aside
      className={`h-full w-full flex-1 flex flex-col min-w-0 min-h-0 self-stretch ${
        isModal ? '' : `border-l ${panelClass}`
      } ${isModal ? panelClass : ''}`}
    >
      <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className={`h-4 w-4 shrink-0 ${lightSurface ? 'text-blue-600' : 'text-blue-300'}`} />
          <h2 className="m-0 text-sm font-semibold truncate">
            {p('header', { docType: docTypeLabel })}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={`p-1.5 rounded-lg border transition-colors ${
            lightSurface
              ? 'border-gray-200 hover:bg-gray-100 text-gray-600'
              : 'border-white/15 hover:bg-white/10 text-white/70'
          }`}
          title={p('close_title')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="relative flex flex-1 min-h-0 min-w-0 flex-col">
        {isFrameLoading && (
          <div
            className={`absolute inset-0 z-10 flex flex-col gap-4 overflow-hidden p-4 sm:p-5 ${
              lightSurface ? 'bg-gray-50' : 'bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900'
            }`}
          >
            <div className="flex items-center gap-3 shrink-0">
              <Loader2
                className={`h-5 w-5 animate-spin shrink-0 ${
                  lightSurface ? 'text-blue-600' : 'text-blue-400'
                }`}
              />
              <p className={`m-0 text-sm font-medium ${lightSurface ? 'text-gray-700' : 'text-white/85'}`}>
                {p('loading')}
              </p>
            </div>
            <div className="space-y-3 flex-1 min-h-0">
              <div
                className={`h-14 rounded-xl animate-pulse ${
                  lightSurface ? 'bg-gray-200' : 'bg-white/10'
                }`}
              />
              <div
                className={`h-40 rounded-xl animate-pulse ${
                  lightSurface ? 'bg-gray-200' : 'bg-white/10'
                }`}
              />
              <div
                className={`h-32 rounded-xl animate-pulse ${
                  lightSurface ? 'bg-gray-200' : 'bg-white/8'
                }`}
              />
              <div
                className={`h-24 rounded-xl animate-pulse ${
                  lightSurface ? 'bg-gray-200' : 'bg-white/8'
                }`}
              />
            </div>
          </div>
        )}

        <iframe
          key={embedSrc}
          title={p('iframe_title', { docType: docTypeLabel })}
          src={embedSrc}
          className={`block flex-1 w-full min-w-0 min-h-0 border-0 transition-opacity duration-300 ${
            isFrameLoading ? 'opacity-0' : 'opacity-100'
          } ${lightSurface ? 'bg-white' : 'bg-slate-900'}`}
          style={{ width: '100%', height: '100%' }}
        />
      </div>
    </aside>
  );
}
